/**
 * Curated icon set for configurable portal navigation (improvement #1).
 *
 * A small, named palette shared by the portal shell (which renders the icon)
 * and the admin nav editor (which offers the picker), so the same names round-
 * trip through `portal_config.nav[].icon`. Names are stable string keys — never
 * store a component reference. Unknown / unset names fall back per node kind.
 */
import {
  BarChart3,
  Bell,
  Calendar,
  ExternalLink,
  FileText,
  Folder,
  Home,
  Inbox,
  LayoutGrid,
  LayoutList,
  type LucideIcon,
  Settings,
  Star,
  Users,
} from "lucide-react";

/** name → component. Keep keys lowercase + stable (they persist to the DB). */
export const NAV_ICONS: Record<string, LucideIcon> = {
  home: Home,
  page: FileText,
  grid: LayoutGrid,
  list: LayoutList,
  chart: BarChart3,
  users: Users,
  calendar: Calendar,
  inbox: Inbox,
  bell: Bell,
  star: Star,
  settings: Settings,
  folder: Folder,
  link: ExternalLink,
};

/** Picker order (also used to render the choices in the editor). */
export const NAV_ICON_NAMES = Object.keys(NAV_ICONS);

/**
 * Resolve a nav node to its icon component. Explicit `icon` wins; otherwise a
 * sensible default per kind (home pages get the house, external links the
 * arrow, groups a folder, plain pages a document).
 */
export function navIcon(node: {
  kind: "page" | "link" | "group";
  icon?: string | null;
  is_home?: boolean;
}): LucideIcon {
  if (node.icon && NAV_ICONS[node.icon]) return NAV_ICONS[node.icon];
  if (node.kind === "link") return ExternalLink;
  if (node.kind === "group") return Folder;
  return node.is_home ? Home : FileText;
}
